/**
 * Accessibility layer for the Warnify (Product Warnings) confirmation popup.
 *
 * Warnify shows its "Preorder" confirmation in a remodal popup (.elspw-modal) that has no dialog role,
 * no modal state, an unnamed close button, no Tab containment, and no focus return. This file only adds
 * ARIA attributes and keyboard focus handling around remodal's own `opened` / `closed` events.
 * It never calls Warnify or remodal APIs, never clicks anything, and never touches the OK / Cancel
 * handlers, line item properties, or the add-to-cart flow (assets/s-product-form.js owns that).
 */
(() => {
  if (window.__gpWarnifyA11y) return;
  window.__gpWarnifyA11y = true;

  const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let opener = null;

  /** Name the popup and its close button; idempotent. @param {HTMLElement} modal */
  const label = (modal) => {
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    const content = modal.querySelector('.elspw-content');
    if (content) {
      if (!content.id) content.id = 'gp-warnify-message';
      modal.setAttribute('aria-describedby', content.id);
    }
    if (!modal.hasAttribute('aria-label') && !modal.hasAttribute('aria-labelledby')) {
      modal.setAttribute('aria-label', document.documentElement.dataset.warnifyDialogLabel || 'Important product notice');
    }
    const close = modal.querySelector('.remodal-close');
    if (close && !close.hasAttribute('aria-label')) {
      close.setAttribute('aria-label', document.documentElement.dataset.warnifyCloseLabel || 'Close');
    }
  };

  /** Keep Tab / Shift+Tab inside the open popup. @param {KeyboardEvent} event */
  const trapTab = (event) => {
    if (event.key !== 'Tab') return;
    const modal = document.querySelector('.elspw-modal.remodal-is-opened, .elspw-modal.remodal-is-opening');
    if (!modal) return;
    const items = [...modal.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (!modal.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && (active === first || active === modal)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const bind = ($) => {
    $(document).on('opening', '.elspw-modal', function () {
      const active = document.activeElement;
      opener = active instanceof HTMLElement && !this.contains(active) ? active : opener;
      label(this);
    });
    $(document).on('opened', '.elspw-modal', function () {
      label(this);
      // Put focus on the primary action so screen readers read the message (aria-describedby) with it.
      const confirm = this.querySelector('.remodal-confirm');
      if (confirm instanceof HTMLElement) confirm.focus();
      document.addEventListener('keydown', trapTab, true);
    });
    $(document).on('closed', '.elspw-modal', function (event) {
      document.removeEventListener('keydown', trapTab, true);
      const reason = event && (event.reason || (event.originalEvent && event.originalEvent.reason));
      const target = opener;
      opener = null;
      // On OK the cart drawer opens and takes focus itself; only return focus on Cancel / Escape / close.
      if (reason === 'confirmation') return;
      if (target && target.isConnected && typeof target.focus === 'function') {
        requestAnimationFrame(() => target.focus({ preventScroll: true }));
      }
    });
  };

  // Warnify loads its own jQuery copy (window.ElspwjQuery) asynchronously; remodal events are jQuery events.
  let tries = 0;
  const wait = () => {
    const $ = window.ElspwjQuery;
    if ($ && typeof $.fn?.on === 'function') return bind($);
    if (++tries < 60) setTimeout(wait, 250);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wait, { once: true });
  else wait();
})();
